// Guide §8.3 — Bible 10.0 (single source) — plan-based feature gating.
// Sale / baki / customer / basic stock / expense / daily khata / invoice list / import & export have NO entitlement check
// (Bible 12.1 rule 1). Everything else needs a plan to *write*; in `free` state old Premium data stays readable (Bible 6.4).
import { CATEGORY_CONFIG } from './categories';
import type { Category, Plan } from './enums';
import type { FeatureFlags } from './featureFlags';
import type { SubscriptionSnapshot } from './billing/resolve';

export const PLAN_RANK: Record<Plan, number> = { free: 0, basic: 1, pro: 2, business: 3, enterprise: 4 };
export const rank = (p: Plan): number => PLAN_RANK[p];

export const MIN_PLAN = {
  returns: 'basic',
  profit_reports: 'basic',
  promotions: 'basic',
  vendor: 'basic',
  stock_investment: 'basic',
  zakat: 'basic',
  barcode_camera: 'basic',
  category_tools_basic: 'basic',
  category_tools_full: 'pro',
  barcode_scanner: 'pro',
  daily_summary_push: 'pro',
  multi_shop_create: 'pro', // a second shop needs Pro or higher (Basic and Free are single-shop); how many is decided by shopLimit()
  combined_summary: 'pro',
  medicine_autofill: 'pro',
  auto_reminder: 'business',
  staff: 'business',
  label_printer: 'business',
  dukaniq_a: 'business',
  dukaniq_b: 'business',
} as const satisfies Record<string, Plan>;

export type Feature = keyof typeof MIN_PLAN;
export const FEATURES = Object.keys(MIN_PLAN) as Feature[];

export const LIMITS = {
  /** Shops included in the plan. Pro and Business: this many + purchased extraShops (৳199 each, same plan). Free and Basic: exactly this many. */
  shopsIncluded: { free: 1, basic: 1, pro: 1, business: 3, enterprise: Infinity },
  staffPerShop: { free: 0, basic: 0, pro: 0, business: 3, enterprise: Infinity },
  freeSmsPoints: { free: 0, basic: 20, pro: 50, business: 150, enterprise: 0 }, // trial: 50
} as const satisfies Record<string, Record<Plan, number>>;

export function effectivePlan(sub: Pick<SubscriptionSnapshot, 'status' | 'plan'>): Plan {
  if (sub.status === 'trial') return 'pro';
  if (sub.status === 'active' || sub.status === 'past_due') return sub.plan;
  return 'free';
}

/** Maximum shops this subscription may own (Personal Space not counted — Bible 9.2). */
export function shopLimit(sub: SubscriptionSnapshot): number {
  const p = effectivePlan(sub);
  if (sub.status === 'trial') return 1; // trial: 1 shop (Bible 6.2)
  if (p === 'pro' || p === 'business') return LIMITS.shopsIncluded[p] + Math.max(0, sub.extraShops); // only Pro and Business can hold extra shops
  return LIMITS.shopsIncluded[p];
}

export const staffLimitPerShop = (sub: SubscriptionSnapshot): number => LIMITS.staffPerShop[effectivePlan(sub)];

/**
 * JSON has no Infinity: `JSON.stringify(Infinity)` is `null`, which a client then compares as 0 ("limit reached").
 * Every limit that leaves the API goes through this: a finite number, or `null` meaning "no limit".
 */
export const wireLimit = (n: number): number | null => (Number.isFinite(n) ? n : null);
/** The other direction: `null` / missing = no limit. */
export const limitFromWire = (n: number | null | undefined): number => (n === null || n === undefined ? Infinity : n);

export interface EntCtx {
  account: { suspendedAt?: Date | string | null };
  sub: SubscriptionSnapshot;
  flags: FeatureFlags;
  category: Category;
  /** DukanIQ Tier B data readiness (Bible 15.2). Computed nightly; once set it never resets. */
  readiness?: { ready: boolean } | null;
}

/** Can this account create/modify data for the feature right now? (Server enforces; the app only mirrors.) */
export function canWrite(f: Feature, c: EntCtx): boolean {
  if (c.account.suspendedAt) return false;
  if (c.sub.status === 'trial' && f === 'multi_shop_create') return false; // trial = 1 shop
  if (rank(effectivePlan(c.sub)) < rank(MIN_PLAN[f])) return false;
  if (f === 'dukaniq_b') return c.readiness?.ready === true;
  if (f === 'zakat') return c.flags.zakat && CATEGORY_CONFIG[c.category].zakatInScope;
  if (f === 'medicine_autofill') return c.flags.medicine_autofill && c.category === 'pharmacy';
  // DukanIQ only runs in `active` / `past_due`, never on trial (Bible 15.2) — trial is Pro-level, so plan check already blocks it
  return true;
}

/**
 * Read access: Vendor, Staff roster, Promotions and Zakat history are always readable (Bible 6.4).
 * Profit reports stay readable in `free` up to the day the subscription lapsed (see `profitReportCutoff`).
 * DukanIQ cards are hidden when free.
 */
export function canRead(f: Feature, c: EntCtx): boolean {
  if (c.account.suspendedAt) return false;
  if (canWrite(f, c)) return true;
  switch (f) {
    case 'vendor':
    case 'staff':
    case 'promotions':
    case 'zakat':
    case 'stock_investment':
    case 'returns':
      return c.sub.status === 'free';
    case 'profit_reports':
      return c.sub.status === 'free';
    default:
      return false;
  }
}

/** Free-state profit reports are cut off at the date the plan lapsed (ISO). null = no cutoff / not applicable. */
export function profitReportCutoff(sub: SubscriptionSnapshot): Date | null {
  if (sub.status !== 'free') return null;
  return sub.lapsedAt ?? null;
}

export type Lock = 'ok' | 'locked_plan' | 'hidden_flag' | 'suspended';

/** UI helper: why is this feature unavailable? Role-based hiding is handled separately via permissions. */
export function featureLock(f: Feature, c: EntCtx): Lock {
  if (c.account.suspendedAt) return 'suspended';
  if (canWrite(f, c)) return 'ok';
  if (f === 'zakat' && (!c.flags.zakat || !CATEGORY_CONFIG[c.category].zakatInScope)) return 'hidden_flag';
  if (f === 'medicine_autofill' && (!c.flags.medicine_autofill || c.category !== 'pharmacy')) return 'hidden_flag';
  return 'locked_plan';
}

/** Minimum plan name to display on the lock/upgrade prompt. */
export const requiredPlanFor = (f: Feature): Plan => MIN_PLAN[f];

/** Full capability map sent to the app by GET /me/capabilities (Guide §8.4). */
export function entitlementMap(c: EntCtx): Record<Feature, Lock> {
  const out = {} as Record<Feature, Lock>;
  for (const f of FEATURES) out[f] = featureLock(f, c);
  return out;
}
